import { useRef, useState } from 'react';
import type { FormEvent, KeyboardEvent, ReactNode } from 'react';
import { Navigate, useLocation, useNavigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faCircleInfo,
  faEye,
  faEyeSlash,
  faRightToBracket,
} from '@fortawesome/free-solid-svg-icons';
import { BrandLogo } from '../components/brand/BrandLogo';
import { BrandMark } from '../components/brand/BrandMark';
import { useAuth } from '../features/auth/useAuth';
import { isApiError } from '../lib/apiError';
import { Button } from '../components/ui/Button';
import { TextField } from '../components/ui/TextField';
import { Alert } from '../components/ui/Alert';
import { homeRouteFor } from '../routes/homeRoute';

interface FieldErrors {
  email?: string;
  password?: string;
}

/**
 * Validacion local del acceso, escrita a mano.
 *
 * Es la unica pantalla del sistema que no usa Zod ni `react-hook-form`, y a
 * proposito: es la primera que carga un visitante sin sesion, y arrastrar dos
 * librerias de formularios para comprobar que dos campos no estan vacios es
 * peso que se paga antes de ver nada. El resto de las pantallas van detras del
 * acceso y se cargan bajo demanda, asi que ahi las librerias no bloquean la
 * primera pintura.
 *
 * La comprobacion del correo es deliberadamente laxa: la definitiva la hace el
 * servidor, y una expresion estricta en el cliente solo sirve para rechazar
 * direcciones validas que nadie previo.
 */
function validate(email: string, password: string): FieldErrors {
  const errors: FieldErrors = {};

  if (email.trim() === '') {
    errors.email = 'Escribe tu correo electrónico.';
  } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim())) {
    errors.email = 'El correo no es válido.';
  }

  if (password === '') {
    errors.password = 'Escribe tu contraseña.';
  }

  return errors;
}

export function LoginPage(): ReactNode {
  const { login, isAuthenticated, user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const emailRef = useRef<HTMLInputElement>(null);
  const passwordRef = useRef<HTMLInputElement>(null);

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [fieldErrors, setFieldErrors] = useState<FieldErrors>({});
  const [formError, setFormError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isPasswordVisible, setIsPasswordVisible] = useState(false);
  const [isCapsLockOn, setIsCapsLockOn] = useState(false);

  if (isAuthenticated && user) {
    return <Navigate to={homeRouteFor(user.role)} replace />;
  }

  async function onSubmit(event: FormEvent<HTMLFormElement>): Promise<void> {
    event.preventDefault();
    setFormError(null);

    const errors = validate(email, password);
    setFieldErrors(errors);
    if (errors.email !== undefined || errors.password !== undefined) {
      // El foco va al primer campo que falla: sin esto, quien navega con
      // teclado o con lector de pantalla se queda al final del formulario
      // leyendo un error que esta dos campos mas arriba.
      (errors.email !== undefined ? emailRef : passwordRef).current?.focus();
      return;
    }

    setIsSubmitting(true);
    try {
      const authenticated = await login(email.trim(), password);
      const from = (location.state as { from?: string } | null)?.from;
      void navigate(from ?? homeRouteFor(authenticated.role), { replace: true });
    } catch (error) {
      // El backend responde lo mismo ante correo desconocido y contrasena
      // incorrecta. La interfaz no debe inventar un mensaje mas especifico.
      setFormError(
        isApiError(error)
          ? error.message
          : 'No se pudo iniciar sesión. Intenta de nuevo en unos minutos.',
      );
    } finally {
      setIsSubmitting(false);
    }
  }

  /**
   * Bloq Mayus encendido con la contrasena tapada.
   *
   * Es la causa mas comun de un fallo de acceso que nadie se explica, y aqui no
   * hay forma de descubrirla: el servidor responde lo mismo ante contrasena
   * incorrecta que ante correo desconocido, a proposito. Avisar de la tecla no
   * revela nada de la cuenta.
   */
  function trackCapsLock(event: KeyboardEvent<HTMLInputElement>): void {
    setIsCapsLockOn(event.getModifierState('CapsLock'));
  }

  return (
    <div className="flex min-h-dvh flex-col bg-background lg:grid lg:grid-cols-2">
      {/*
        Un solo bloque de marca para las dos formas de la pantalla: barra
        superior en telefono y tablet, columna completa a partir de 1024 px.
        Duplicarlo en dos elementos, uno oculto en cada tamano, era mantener el
        mismo texto en dos sitios y verlo dos veces en el lector de pantalla.
      */}
      <aside className="relative isolate flex shrink-0 items-center overflow-hidden border-b-2 border-accent bg-primary px-4 py-4 text-white lg:min-h-dvh lg:justify-center lg:border-b-0 lg:border-r-2 lg:px-10 lg:py-14">
        {/*
          Filigrana: el propio isotipo, enorme y casi apagado, saliendose por el
          borde.
        *
          La columna es media pantalla para un solo logotipo, y vacia se leia
          como un fallo de carga. Se llena con la geometria de la marca en vez
          de con adornos: es plana, sin degradados ni sombras, como pide la
          identidad, y a este contraste no compite con el logotipo ni con el
          formulario de al lado.

          Recortada por `overflow-hidden` del contenedor, asi que crece sin
          empujar nada ni provocar desplazamiento lateral.
        */}
        <BrandMark className="pointer-events-none absolute -left-[22%] top-1/2 hidden h-[150%] w-auto -translate-y-1/2 text-white opacity-[0.055] lg:block" />

        <BrandLogo title="Grupo ADCO" className="relative h-9 w-auto lg:h-28" />
      </aside>

      {/*
        En escritorio el formulario deja de ser una tarjeta y se apoya en la
        superficie blanca de su columna: dentro de media pantalla vacia, un
        recuadro gris dibujaba un borde que no separa nada. En movil la tarjeta
        se queda, porque ahi si delimita el formulario contra el fondo.
      */}
      <main className="flex flex-1 items-center justify-center px-4 py-10 sm:px-6 lg:bg-surface lg:px-10">
        <div className="w-full max-w-sm">
          <h1 className="text-xl font-semibold text-content lg:text-2xl">Inicia sesión</h1>
          <p className="mt-1 text-sm text-muted">
            Entra con la cuenta que te dio el administrador.
          </p>

          <form
            onSubmit={(event) => {
              void onSubmit(event);
            }}
            noValidate
            className="mt-5 space-y-4 rounded-card border border-line bg-surface p-5 sm:p-6 lg:mt-7 lg:rounded-none lg:border-0 lg:bg-transparent lg:p-0"
          >
            {formError ? <Alert tone="danger">{formError}</Alert> : null}

            <TextField
              ref={emailRef}
              label="Correo electrónico"
              name="email"
              type="email"
              autoComplete="username"
              autoFocus
              placeholder="nombre@empresa.com"
              value={email}
              error={fieldErrors.email}
              onChange={(event) => {
                setEmail(event.target.value);
                // El error se retira en cuanto se corrige el campo. Mantenerlo
                // hasta el siguiente envio deja en rojo algo que ya esta bien.
                setFieldErrors((errors) => ({ ...errors, email: undefined }));
              }}
            />

            <TextField
              ref={passwordRef}
              label="Contraseña"
              name="password"
              type={isPasswordVisible ? 'text' : 'password'}
              autoComplete="current-password"
              placeholder="Tu contraseña"
              value={password}
              error={fieldErrors.password}
              hint={isCapsLockOn ? 'Bloq Mayús está activado.' : undefined}
              onKeyUp={trackCapsLock}
              onKeyDown={trackCapsLock}
              onBlur={() => {
                setIsCapsLockOn(false);
              }}
              onChange={(event) => {
                setPassword(event.target.value);
                setFieldErrors((errors) => ({ ...errors, password: undefined }));
              }}
              trailing={
                /*
                 * Ver lo tecleado no es comodidad: el chofer escribe su
                 * contrasena de pie, con guantes y con el sol de frente, y sin
                 * poder mirarla el unico camino es fallar y volver a empezar.
                 * Empieza tapada, que es lo que debe mostrarse por defecto.
                 */
                <button
                  type="button"
                  onClick={() => {
                    setIsPasswordVisible((visible) => !visible);
                  }}
                  aria-label={isPasswordVisible ? 'Ocultar contraseña' : 'Mostrar contraseña'}
                  className="flex h-11 w-11 items-center justify-center rounded-card text-muted transition-colors hover:text-content"
                >
                  <FontAwesomeIcon
                    icon={isPasswordVisible ? faEyeSlash : faEye}
                    aria-hidden="true"
                  />
                </button>
              }
            />

            <Button type="submit" fullWidth size="lg" isLoading={isSubmitting}>
              {isSubmitting ? null : (
                <FontAwesomeIcon icon={faRightToBracket} aria-hidden="true" className="w-4" />
              )}
              {isSubmitting ? 'Entrando…' : 'Entrar'}
            </Button>
          </form>

          <p className="mt-6 flex items-start gap-2 text-xs text-muted">
            <FontAwesomeIcon
              icon={faCircleInfo}
              aria-hidden="true"
              className="mt-0.5 w-3.5 shrink-0"
            />
            <span>¿No recuerdas tu contraseña? Pídele al administrador que la restablezca.</span>
          </p>
        </div>
      </main>
    </div>
  );
}
